import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Box, Flex, IconButton } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { MdArrowUpward } from 'react-icons/md';
import AGGridBase from 'components/tables/AGGrid/AGGridBase';
import { descriptionStyling } from 'components/forms/new/formStyles';
import { Text, Tooltip } from '@/components/v4';
import {
    PANE_HEIGHT,
    PANE_MIN_HEIGHT,
    SCROLL_TOP_THRESHOLD,
    TOOLBAR_BAND_BG,
} from '../constants';
import { defaultColDef } from './tableConfig';
import RowHoverTooltip from './RowHoverTooltip';

/**
 * One list on the bulk operations page, laid out like the Numbers page
 * table: a heading (with an optional info tooltip, a count and a note beside
 * it), a line of help text, then a bordered table with an optional grey
 * toolbar band, the grid and a footer. Both lists use this, so their borders
 * and footers match in every view. `emptyMessage` shows over the grid, e.g.
 * when filters leave nothing to list, and `tableSx` adds styles to the table.
 * Give both lists the same `hasDescriptionLine` so their tables stay level.
 * `getRowTooltip(rowData)` gives a tooltip for anywhere on a row.
 */
const NumbersPane = ({
    title,
    info,
    count,
    countNote,
    description,
    hasDescriptionLine,
    emptyMessage,
    tableSx,
    getRowTooltip,
    toolbar,
    footer,
    gridRef,
    gridProps,
    ...boxProps
}) => {
    const { darkMode, colorScheme } = useSelector((state) => state.settings);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const gridBoxRef = useRef(null);
    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const bandBg = darkMode ? TOOLBAR_BAND_BG.dark : TOOLBAR_BAND_BG.light;

    return (
        <Flex
            direction="column"
            h={PANE_HEIGHT}
            minH={PANE_MIN_HEIGHT}
            minW={0}
            aria-label={title}
            {...boxProps}>
            {/* On a narrow list the count and note wrap below the title */}
            <Flex
                align="center"
                wrap="wrap"
                columnGap={2}
                rowGap={1}
                mb={hasDescriptionLine ? 1 : 3}>
                <Text fontSize="md" fontWeight="500" whiteSpace="nowrap">
                    {title}
                </Text>
                {info && (
                    <Tooltip hasArrow placement="top" label={info}>
                        <InfoOutlineIcon
                            aria-label={`About ${title}`}
                            color={darkMode ? 'gray.300' : 'gray.500'}
                        />
                    </Tooltip>
                )}
                <Badge borderRadius="full" px={2} textTransform="none">
                    {count}
                </Badge>
                {countNote && (
                    <Text
                        fontSize="xs"
                        color={darkMode ? 'gray.300' : 'gray.500'}
                        whiteSpace="nowrap">
                        {countNote}
                    </Text>
                )}
            </Flex>
            {/* Two lines kept in every pane while the lists sit side by side
                (from lg), so the tables stay level when the help text wraps */}
            {hasDescriptionLine && (
                <Text
                    mb={3}
                    minH={{ base: 0, lg: '24px' }}
                    sx={{
                        ...descriptionStyling,
                        color: darkMode ? 'gray.100' : '#757575',
                    }}>
                    {description}
                </Text>
            )}

            <Flex
                direction="column"
                flex="1"
                minH={0}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="10px"
                overflow="hidden"
                // Set on the grid's own theme element, which otherwise applies
                // the material theme's purple selected-row colour. The dark
                // value is rgba(144, 205, 244, 0.16) as hex: Chakra splits
                // commas in custom property values.
                sx={{
                    '.ag-theme-base': {
                        '--ag-selected-row-background-color': darkMode
                            ? '#90CDF429'
                            : 'var(--chakra-colors-blue-50)',
                    },
                    ...tableSx,
                }}>
                {toolbar && <Box bg={bandBg}>{toolbar}</Box>}

                <Box ref={gridBoxRef} flex="1" minH={0} position="relative">
                    <AGGridBase
                        gridRef={gridRef}
                        defaultColDef={defaultColDef}
                        pagination={false}
                        useFixedHeight={false}
                        customHeight="100%"
                        rowHeight={48}
                        onBodyScroll={(event) =>
                            setShowBackToTop(event.top > SCROLL_TOP_THRESHOLD)
                        }
                        {...gridProps}
                    />
                    <RowHoverTooltip
                        containerRef={gridBoxRef}
                        gridRef={gridRef}
                        getLabel={getRowTooltip}
                    />
                    {emptyMessage && (
                        <Flex
                            position="absolute"
                            inset={0}
                            align="center"
                            justify="center"
                            px={6}
                            textAlign="center"
                            pointerEvents="none">
                            <Text fontSize="sm">{emptyMessage}</Text>
                        </Flex>
                    )}
                    {showBackToTop && (
                        <Tooltip label="Back to top" hasArrow placement="left">
                            <IconButton
                                aria-label="Back to top"
                                icon={<MdArrowUpward />}
                                isRound
                                size="sm"
                                colorScheme={colorScheme}
                                position="absolute"
                                right={4}
                                bottom={4}
                                zIndex={1}
                                boxShadow="md"
                                onClick={() =>
                                    gridRef.current?.api?.ensureIndexVisible(
                                        0,
                                        'top',
                                    )
                                }
                            />
                        </Tooltip>
                    )}
                </Box>

                <Flex
                    align="center"
                    justify="space-between"
                    gap={3}
                    px={4}
                    py={3}
                    bg={bandBg}
                    borderTop="1px solid"
                    borderColor={borderColor}>
                    {footer}
                </Flex>
            </Flex>
        </Flex>
    );
};

export default NumbersPane;
